/**
 * Animated voice orb visualization
 *
 * Visual feedback for voice chat state:
 * - Idle: Gray, static
 * - Listening: brand mint, ripple pulse
 * - Thinking: brand orange, gentle pulse
 * - Speaking: brand accent, wave animation
 */

import React from 'react';

export type OrbState = 'idle' | 'listening' | 'thinking' | 'speaking' | 'connecting' | 'error';

interface VoiceOrbProps {
  state: OrbState;
  size?: 'sm' | 'md' | 'lg';
  onClick?: () => void;
  isMicMuted?: boolean;
}

const stateColors: Record<OrbState, { bg: string; ring: string; glow: string }> = {
  idle: {
    bg: 'bg-[#9a9a9a]',
    ring: 'ring-[#d2d2d2]',
    glow: 'shadow-[#9a9a9a]/30',
  },
  connecting: {
    bg: 'bg-[#e0a240]',
    ring: 'ring-[#f3c173]',
    glow: 'shadow-[#e0a240]/45',
  },
  listening: {
    bg: 'bg-[#28d1a2]',
    ring: 'ring-[#7be6c9]',
    glow: 'shadow-[#28d1a2]/45',
  },
  thinking: {
    bg: 'bg-[#ed7203]',
    ring: 'ring-[#e0a240]',
    glow: 'shadow-[#ed7203]/45',
  },
  speaking: {
    bg: 'bg-[#15779b]',
    ring: 'ring-[#4aa6c7]',
    glow: 'shadow-[#15779b]/45',
  },
  error: {
    bg: 'bg-red-500',
    ring: 'ring-red-400',
    glow: 'shadow-red-500/50',
  },
};

const sizeClasses = {
  sm: 'w-20 h-20',
  md: 'w-32 h-32',
  lg: 'w-48 h-48',
};

export const VoiceOrb: React.FC<VoiceOrbProps> = ({ state, size = 'lg', onClick, isMicMuted = false }) => {
  const colors = state === 'listening' && isMicMuted
    ? {
        bg: 'bg-gray-500',
        ring: 'ring-gray-400',
        glow: 'shadow-gray-500/40',
      }
    : stateColors[state];
  const sizeClass = sizeClasses[size];
  const ariaLabel = state === 'listening'
    ? isMicMuted
      ? 'Voice state: listening muted. Click to unmute microphone'
      : 'Voice state: listening active. Click to mute microphone'
    : `Voice state: ${state}. Click to ${state === 'speaking' ? 'interrupt' : 'interact'}`;

  return (
    <div
      className={`relative ${sizeClass} cursor-pointer`}
      onClick={onClick}
      role="button"
      aria-label={ariaLabel}
    >
      {/* Outer glow ring - animated */}
      <div
        className={`
          absolute inset-0 rounded-full
          ${colors.ring} ring-4
          ${colors.glow} shadow-2xl
          ${state === 'listening' && 'animate-ping'}
          ${state === 'thinking' && 'animate-pulse'}
          ${state === 'speaking' && 'animate-pulse'}
          ${state === 'connecting' && 'animate-pulse'}
        `}
        style={{
          animationDuration: state === 'listening' ? '1.5s' : '1s',
        }}
      />

      {/* Middle ring */}
      <div
        className={`
          absolute inset-2 rounded-full
          ${colors.bg} opacity-30
          ${state === 'speaking' && 'animate-[ping_1s_ease-out_infinite]'}
        `}
      />

      {/* Inner orb */}
      <div
        className={`
          absolute inset-4 rounded-full
          ${colors.bg}
          ${colors.glow} shadow-lg
          transition-all duration-300
          ${state !== 'idle' && state !== 'error' && 'animate-pulse'}
        `}
      >
        {/* Wave bars for speaking state */}
        {state === 'speaking' && (
          <div className="absolute inset-0 flex items-center justify-center gap-1">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="w-1.5 bg-white rounded-full animate-bounce"
                style={{
                  height: `${20 + Math.random() * 30}%`,
                  animationDelay: `${i * 0.1}s`,
                  animationDuration: '0.5s',
                }}
              />
            ))}
          </div>
        )}

        {/* Microphone icon for listening */}
        {state === 'listening' && (
          <div className="absolute inset-0 flex items-center justify-center">
            <svg
              className="w-1/3 h-1/3 text-white"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm-1 1.93c-3.94-.49-7-3.85-7-7.93h2c0 3.31 2.69 6 6 6s6-2.69 6-6h2c0 4.08-3.06 7.44-7 7.93V19h4v2H8v-2h4v-3.07z" />
              {isMicMuted && (
                <path
                  d="M5 5L19 19"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.25}
                  strokeLinecap="round"
                />
              )}
            </svg>
          </div>
        )}

        {/* Spinner for connecting/thinking */}
        {(state === 'connecting' || state === 'thinking') && (
          <div className="absolute inset-0 flex items-center justify-center">
            <svg
              className="w-1/3 h-1/3 text-white animate-spin"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>
        )}

        {/* Error icon */}
        {state === 'error' && (
          <div className="absolute inset-0 flex items-center justify-center">
            <svg
              className="w-1/3 h-1/3 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
        )}
      </div>

      {/* State label removed — VoiceChatModal renders German status text instead */}
    </div>
  );
};

export default VoiceOrb;
